import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from 'xlsx';

import {
  Button,
  Col,
  Collapse,
  Divider,
  Flex,
  List,
  Row,
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
  theme,
  message,
} from "antd";

import { CaretRightOutlined, DownloadOutlined } from "@ant-design/icons";

import {
  updateJobMappings,
  updateOrganization,
} from "../../redux/organizationSlice";
import JobMapping from "./jobMapping";
import { useCallback } from "react";
import SaveButton from "../UI/savebutton";

const { Title, Text } = Typography;

const CreateJobMapping = () => {
  const [offeredJobs, setOfferedJobs] = useState([]);
  const [isStateUpdate, setIsStateUpdate] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [jobItemsData, setJobItemsData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { token } = theme.useToken();

  const {
    organizationData,
    serviceTitanBusinessUnits,
    serviceTitanJobs,
    loading,
    serviveTitanLoading,
  } = useSelector((state) => state?.organization);
  const { trades = [], services = [] } = useSelector((state) => state?.trade);
  const { jobs: backendJobs = [] } = useSelector((state) => state?.jobs);

  const dispatch = useDispatch();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const excelData = [];
      const isHCP = organizationData?.organization_provider === 'HCP';

      const headers = [
        'tradeName',
        'serviceName',
        'jobName',
        'is_active',
        'use_default_dispatch',
        'availability_mode'
      ];

      if (isHCP) {
        headers.push(
          'dispatch_fee',
          'unit_price',
          'st_non_member_job_name',
          'dispatch_header',
          'dispatch_body',
          'dispatch_text'
        );
      } else {
        headers.push(
          'member_dispatch_fee',
          'dispatch_fee',
          'st_business_unit_name',
          'is_member',
          'st_non_member_job_name',
          'st_member_job_name',
          'dispatch_header',
          'dispatch_body',
          'dispatch_text'
        );
      }

      excelData.push(headers);

      organizationData?.jobs_mapping?.forEach(mapping => {
        const tradeInfo = trades.find(t => t._id === mapping.trade);
        const tradeName = tradeInfo?.tradeName || "Unknown Trade";

        Object.entries(mapping).forEach(([serviceType, jobs]) => {
          if (serviceType !== 'trade' && serviceType !== 'additionalServices' && Array.isArray(jobs)) {
            jobs.forEach(job => {
              const jobInfo = backendJobs.find(j => j._id === job.jobItem);
              const serviceInfo = services.find(s => s.id === serviceType);

              if (!job.is_active || !jobInfo) return;

              const rowData = [
                tradeName,
                serviceInfo?.name || "Unknown Service",
                jobInfo?.jobItem,
                job.is_active,
                job.use_default_dispatch,
                job.availability_mode === 'office_hours' ? 'Office Hours' : '24/7'
              ];

              if (isHCP) {
                rowData.push(
                  job.dispatch_fee,
                  job.unit_price,
                  job.st_non_member_job_name,
                  job.use_default_dispatch ? organizationData.default_dispatch_header : job.dispatch_header,
                  job.use_default_dispatch ? organizationData.default_dispatch_body : job.dispatch_body,
                  job.use_default_dispatch ? organizationData.default_dispatch_text : job.dispatch_text
                );
              } else {
                rowData.push(
                  job.member_dispatch_fee,
                  job.dispatch_fee,
                  job.st_business_unit_name,
                  job.is_member,
                  job.st_non_member_job_name,
                  job.st_member_job_name,
                  job.use_default_dispatch ? organizationData.default_dispatch_header : job.dispatch_header,
                  job.use_default_dispatch ? organizationData.default_dispatch_body : job.dispatch_body,
                  job.use_default_dispatch ? organizationData.default_dispatch_text : job.dispatch_text
                );
              }

              excelData.push(rowData);
            });
          }
        });
      });


      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      const colWidths = headers.map((header) => {
        if (header.includes('Body')) return { wch: 40 };
        if (header.includes('Header') || header.includes('Checkbox')) return { wch: 30 };
        if (header.includes('Type') || header.includes('Unit')) return { wch: 20 };
        if (header.includes('Fee')) return { wch: 15 };
        return { wch: 15 };
      });

      ws['!cols'] = colWidths;

      const headerRange = XLSX.utils.decode_range(ws['!ref']);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "CCCCCC" } },
          alignment: { horizontal: "center" }
        };
      }

      XLSX.utils.book_append_sheet(wb, ws, "Job Mappings");
      await new Promise(resolve => setTimeout(resolve, 800));
      const orgName = (organizationData?.organization_name || "Organization")
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .trim();
      const providerType = isHCP ? 'HCP' : 'ST';
      const fileName = `${orgName}_${providerType}_Job_Mapping_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success('Job mappings exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Failed to export job mappings');
    } finally {
      setIsExporting(false);
    }
  };

  const onChange = (value, jobItemId) => {
    setIsDirty(true)
    const jobExists = jobItemsData.findIndex((e) => e?.jobItem === jobItemId);
    if (jobItemsData?.length && value && jobExists !== -1) {
      setJobItemsData(
        jobItemsData.map((item) => {
          if (item?.jobItem === jobItemId) {
            return {
              ...item,
              ...value,
            };
          } else {
            return item;
          }
        })
      );
    } else {
      const data = {
        jobItem: jobItemId,
        ...value,
      };
      setJobItemsData((jobItemsData) => [...jobItemsData, data]);

    }
  };

  const onSave = () => {
    dispatch(
      updateJobMappings({ selectedTrade, services, serviceType, jobItemsData })
    );
    setIsStateUpdate(true);
    setIsDirty(false)
  };

  useEffect(() => {
    if (selectedTrade && serviceType && organizationData) {
      let data =
        organizationData?.jobs_mapping?.find(
          (e) => e.trade === selectedTrade
        )?.[serviceType] || [];
      if (data && data?.length === 0) {
        data = organizationData?.jobs_mapping?.find(
          (e) => e.trade === selectedTrade
        )?.additionalServices?.[serviceType]
      }
      if (data) {
        setJobItemsData([...data]);
      }
      const jobsData = backendJobs.filter(
        (item) =>
          item?.trade?._id === selectedTrade &&
          item?.serviceType?.toLowerCase() === serviceType?.toLowerCase(),
      );
      setOfferedJobs(jobsData);
    }
    if (isStateUpdate) {
      dispatch(
        updateOrganization({
          id: organizationData._id,
          payload: {
            jobs_mapping: organizationData?.jobs_mapping,
          },
          page: "Job Mapping"
        })
      );
      setIsStateUpdate(false);
    }
  }, [
    dispatch,
    organizationData,
    selectedTrade,
    serviceType,
    isStateUpdate,
    backendJobs,
  ]);

  const filterOption = useCallback(
    (input, option) =>
      (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
    []
  );

  const getAssociatedQuestions = useCallback(
    (item) => {
      const Questions = [];
      organizationData?.questions?.forEach((question) => {
        const jobMatched = question?.associateJobs.findIndex(
          (e) =>
            e?.trade?.tradeName.toLowerCase() ===
            item?.trade?.tradeName?.toLowerCase() &&
            e?.serviceType.findIndex(
              (ser) => ser.toLowerCase() === item?.serviceType?.toLowerCase()
            ) !== -1 &&
            e?.job?.toLowerCase() === item?.jobItem?.toLowerCase()
        );
        if (jobMatched !== -1) {
          const questionObj = {
            key: question?.question,
            label: question?.question,
            children: (
              <div style={{ paddingLeft: 24 }}>
                {question?.options?.map((option) => (
                  <Tag color="geekblue" key={option}>
                    {option?.option}
                  </Tag>
                ))}
              </div>
            ),
            style: {
              marginBottom: 10,
              background: token.colorFillAlter,
              borderRadius: token.borderRadiusLG,
              border: "none",
            },
          };
          Questions.push(questionObj);
        }
      });
      return Questions;
    },
    [organizationData?.questions]
  );

  const tradeHashMap = useMemo(() => {
    const map = {};
    organizationData?.navigation_mapping
      ?.filter(trade => trade.isActive === true)
      ?.forEach(trade => {
        const orgTrade = trades.find(t => t.tradeName === trade.trade.name);

        if (orgTrade) {
          const services = {}
          if (trade.offeredServiceTypes.repair) {
            services.repair = { label: "Repair", value: "repair" };
          }
          if (trade.offeredServiceTypes.estimate) {
            services.estimate = { label: "Estimate", value: "estimate" };
          }
          if (trade.offeredServiceTypes.maintenance) {
            services.maintenance = { label: "Maintenance", value: "maintenance" };
          }
          const additionalServices = trade.offeredServiceTypes.additionalServices || {};
          Object.keys(additionalServices).forEach(serviceName => {
            if (additionalServices[serviceName]) {
              services[serviceName] = {
                label: serviceName.charAt(0).toUpperCase() + serviceName.slice(1),
                value: serviceName
              };
            }
          });
          map[orgTrade._id] = {
            tradeName: trade.trade.name,
            tradeId: trade.trade.id,
            _id: orgTrade._id,
            services: services
          };
        }
      });

    return map;
  }, [organizationData?.navigation_mapping, trades]);

  const activeTrades = useMemo(() =>
    Object.keys(tradeHashMap).map(id => ({
      label: tradeHashMap[id].tradeName,
      value: id
    })),
    [tradeHashMap]
  );
  const getActiveServices = useCallback(() => {
    if (!selectedTrade || !tradeHashMap[selectedTrade]) return [];

    return Object.values(tradeHashMap[selectedTrade].services);
  }, [selectedTrade, tradeHashMap]);

  return (
    <>
      <Spin spinning={loading || serviveTitanLoading} fullscreen />
      <Title level={3} style={{ marginBottom: "50px" }}>
        Job Mapping
      </Title>
      <Flex vertical gap={20} justify="center">
        <Text type="secondary" strong underline>
          Autobot.ai Trades Offering
        </Text>
        <Flex justify="space-between" align="flex-start">
          <Flex vertical gap={10}>
            <Flex gap={20}>
              <Select
                showSearch
                placeholder="Select a Trade"
                optionFilterProp="children"
                onChange={(e) => setSelectedTrade(e)}
                filterOption={filterOption}
                options={activeTrades}
                size="middle"
                style={{ width: 250 }}
              />

              <Select
                showSearch
                placeholder="Select Service Type"
                optionFilterProp="children"
                onChange={(e) => {
                  setServiceType(e);
                }}
                filterOption={filterOption}
                options={getActiveServices()}
                size="middle"
                style={{ width: 200 }}
              />
            </Flex>
            <Text type="danger" strong underline italic>
              * Save your changes before switching trades and service type
            </Text>
          </Flex>
          <Button
            type="primary"
            style={{
              marginTop: 4,
              backgroundColor: '#52c41a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export Mappings'}
          </Button>
        </Flex>
      </Flex>
      <Divider orientation="left" style={{ margin: 4 }}>Selected trade job items</Divider>
      {selectedTrade.length && serviceType.length ? (
        offeredJobs?.map((item) => {
          const jobId = item?._id;
          const mappedData = jobItemsData?.find((e) => e.jobItem === jobId);
          const AssociatedQuestions = getAssociatedQuestions(item);

          return (
            <Row key={jobId}>
              <Col lg={24} xl={{ span: 16, offset: 5 }}>
                <div
                  style={{
                    width: "100%",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "8px",
                    backgroundColor: "#fff",
                    marginBottom: "20px",
                  }}
                >
                  <Collapse
                    bordered={false}
                    expandIcon={({ isActive }) => (
                      <CaretRightOutlined rotate={isActive ? 90 : 0} />
                    )}
                    style={{ background: token.colorBgContainer }}
                  >
                    <Collapse.Panel
                      key={jobId}
                      header={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Text
                            type="secondary"
                            strong
                            style={{ padding: "2px 0" }}
                          >
                            {item.jobItem || "Job Details"}
                          </Text>
                          <Flex
                            onClick={(e) => e.stopPropagation()}
                            align="center"
                            style={{ height: "100%" }}
                          >
                            <Switch
                              size="middle"
                              checked={mappedData?.is_active}
                              style={{ width: 35 }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                onChange({ is_active: e }, jobId)
                              }
                            />
                          </Flex>
                        </div>
                      }
                      extra={() => (
                        <Flex align="center" style={{ height: "100%" }}>
                          <Switch
                            size="middle"
                            checked={mappedData?.is_active}
                            style={{ width: 35 }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              onChange({ is_active: e }, jobId)
                            }
                          />
                        </Flex>
                      )}
                    >
                      <Flex
                        align="flex-start"
                        gap={10}
                        style={{ width: "100%" }}
                        vertical
                      >
                        <JobMapping
                          key={jobId}
                          jobId={jobId}
                          mappedData={mappedData}
                          serviceTitanBusinessUnits={
                            serviceTitanBusinessUnits
                          }
                          serviceTitanJobs={serviceTitanJobs}
                          filterOption={filterOption}
                          onChange={onChange}
                        />
                        <div
                          style={{
                            width: "100%",
                            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px",
                            padding: "24px",
                            marginBottom: "20px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                          }}
                        >
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "20px",
                            paddingBottom: "12px",
                            borderBottom: "2px solid #e2e8f0"
                          }}>
                            <div style={{
                              width: "4px",
                              height: "20px",
                              background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                              borderRadius: "2px",
                              marginRight: "12px"
                            }} />
                            <Text
                              type="secondary"
                              strong
                              style={{
                                fontSize: "16px",
                                color: "#1e293b",
                                fontWeight: 600,
                                margin: 0
                              }}
                            >
                              {AssociatedQuestions?.length > 0 ? "Associated Questions" : "No Associated Questions For This Job"}
                            </Text>
                          </div>

                          {AssociatedQuestions?.length > 0 && (
                            <div style={{
                              background: "#ffffff",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              padding: "16px",
                              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
                            }}>
                              <Collapse
                                bordered={false}
                                expandIcon={({ isActive }) => (
                                  <CaretRightOutlined
                                    rotate={isActive ? 90 : 0}
                                    style={{ color: "#3b82f6" }}
                                  />
                                )}
                                style={{
                                  background: "transparent",
                                }}
                                items={AssociatedQuestions}
                              />
                            </div>
                          )}
                        </div>
                      </Flex>
                    </Collapse.Panel>
                  </Collapse>
                </div>
              </Col>
            </Row>
          );
        })
      ) : (
        <List dataSource={[]} />
      )}
      <SaveButton onSubmit={onSave} isDirty={isDirty} />
    </>
  );
};

export default CreateJobMapping;
